import { ArrowLeftOutlined } from '@ant-design/icons'
import { useHistory, useParams } from 'react-router-dom'
import {
  Button,
  Col,
  ContentBody,
  ContentHeader,
  Row,
  OpportunityForm,
  useAuthContext
} from '../../../core/components'

import useOrderCreate from '../../../services/mutations/useOrderCreate'
import { useImmer } from 'use-immer'
import { Product } from '../../../entities/product'
import useOpportunityUpdate from '../../../services/mutations/useOpportunityUpdate'
import useOpportunity from '../../../services/queries/opportunity/useOpportunity'
import { useEffect } from 'react'
import dayjs from 'dayjs'
import useOpportunityOrder from '../../../services/queries/opportunity/useOpportunityOrder'
import useOrderUpdate from '../../../services/mutations/useOrderUpdate'
import { dateFormat } from '../../../utils/format/dateFormat'

const opportunityDefaultValue = {
  id: '',
  businessUnityId: '',
  customerId: '',
  sellerId: '',
  marketId: '',
  situationId: '10',
  contacterId: '',
  contact: '',
  status: 'E',
  nextContact: '',
  salesForecast: '',
  proposalValidity: '',
  eventId: '',
  conceptId: '',
  orderId: ''
  // campaignId: ''
}

const orderDefaultValues = {
  customerId: '',
  budgetId: '',
  salesmanId: '',
  paymentMethodId: '',
  paymentTermId: '',
  businessUnitId: '',
  shippingType: '0',
  statusId: '10',
  invoiceType: 'N',
  total: '',
  approved: 0,
  canceled: 0,
  observation: '',
  deliveryDate: '',
  approvedDate: '',
  items: []
}

const formDefaultValues = {
  opportunity: opportunityDefaultValue,
  order: orderDefaultValues,
  temp: { address: '' }
}

export const EditOpportunityPage = () => {
  const { goBack } = useHistory()
  const { user } = useAuthContext()
  const [formValues, setFormValues] = useImmer(formDefaultValues)
  const { id }: { id: string } = useParams()

  const { data: opportunity } = useOpportunity(id)
  const { data: order } = useOpportunityOrder({
    page: 1,
    limit: 10,
    id
  })
  useEffect(() => {
    setFormValues((draft: any) => {
      draft.opportunity.conceptId = opportunity?.data?.concept?.id
      draft.opportunity.contact = opportunity?.data?.contact
      draft.opportunity.businessUnityId = opportunity?.data?.businessUnity?.id
      draft.opportunity.eventId = opportunity?.data?.event?.id
      // draft.opportunity.campaignId = opportunity?.data?.campaign?.id
      draft.opportunity.statusId = opportunity?.data?.status?.id
      draft.opportunity.customerId = opportunity?.data?.customer?.id
      draft.opportunity.situationId = opportunity?.data?.situation?.id
      draft.opportunity.contacterId = opportunity?.data?.contacter?.id
      draft.opportunity.seller = opportunity?.data?.seller?.id
      draft.opportunity.market = opportunity?.data?.market?.id
      draft.opportunity.nextContact = dayjs(opportunity?.data?.nextContact)
      draft.opportunity.salesForecast = dayjs(opportunity?.data?.salesForecast)
      draft.opportunity.proposalValidity = dayjs(
        opportunity?.data?.proposalValidity
      )
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunity])

  useEffect(() => {
    setFormValues((draft: any) => {
      draft.order.customerId = order?.data?.[0]?.customer.id
      draft.order.salesmanId = order?.data?.[0]?.salesman.id
      draft.order.budgetId = order?.data?.[0]?.budgetId
      draft.order.paymentMethodId = order?.data?.[0]?.paymentMethod?.id
      draft.order.paymentTermId = order?.data?.[0]?.paymentTerm?.id
      draft.order.businessUnitId = order?.data?.[0]?.businessUnit.id
      draft.order.shippingType = orderDefaultValues.shippingType
      draft.order.statusId = orderDefaultValues.statusId
      draft.order.invoiceType = orderDefaultValues.invoiceType
      draft.order.total = order?.data?.[0]?.total
      draft.order.approved = orderDefaultValues.approved
      draft.order.canceled = orderDefaultValues.canceled
      draft.order.observation = order?.data?.[0]?.observation
      draft.order.deliveryDate = orderDefaultValues.deliveryDate
      draft.order.approvedDate = orderDefaultValues.approvedDate
      draft.order.items = order?.data?.[0]?.items?.map((item: any) => ({
        ...item,
        ...item.product,
        price: item.salePrice
      }))
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order])

  const { mutateAsync: orderCreate, reset: orderReset } = useOrderCreate()
  const { mutateAsync: orderUpdate } = useOrderUpdate()

  const { mutate: opportunityMutate } = useOpportunityUpdate()

  const handleSubmit = async () => {
    let orderID: any = ''
    const isNewOrder = !formValues.order.budgetId
    if (formValues.order.items.length > 0 && formValues.order.paymentMethodId) {
      const items = formValues.order.items.map(
        (item: Product, index: number) => ({
          ...item,
          sequence: index + 1,
          productId: item.id,
          quantity: 1,
          salePrice: item.price,
          defaultPrice: item.price,
          priceListId: '',
          discountPercent: 0
        })
      )
      if (!isNewOrder) {
        await orderUpdate({
          ...formValues.order,
          items,
          salesmanId: user?.id || '',
          customerId: formValues.opportunity.customerId,
          // businessUnitId: formValues.opportunity.businessUnityId,
          total: formValues.order.items?.reduce(
            (acc: any, cur: any) => acc + cur.price,
            0
          )
        })
      } else {
        orderID = await orderCreate({
          ...formValues.order,
          items,
          salesmanId: user?.id || '',
          customerId: formValues.opportunity.customerId,
          // businessUnitId: formValues.opportunity.businessUnityId,
          total: formValues.order.items?.reduce(
            (acc: any, cur: any) => acc + cur.price,
            0
          )
        })
      }
      opportunityMutate({
        ...formValues.opportunity,
        id: Number(id),
        contacterId: user?.id || '',
        salesForecast: formValues.opportunity.salesForecast
          ? dateFormat(formValues.opportunity.salesForecast)
          : null,
        sellerId: user?.id || '',
        orderId: isNewOrder ? orderID?.data : formValues.order.budgetId || ''
      })
    } else {
      opportunityMutate({
        ...formValues.opportunity,
        id: Number(id),
        contacterId: user?.id || '',
        sellerId: user?.id || '',
        salesForecast: formValues.opportunity.salesForecast
          ? dateFormat(formValues.opportunity.salesForecast)
          : null
      })
    }
    orderReset()
    goBack()
    setFormValues(formDefaultValues)
  }

  return (
    <>
      <ContentHeader>
        <Button type="text" onClick={goBack}>
          <ArrowLeftOutlined />
        </Button>
        Nova Oportunidade
      </ContentHeader>
      <ContentBody>
        <div>
          <OpportunityForm values={formValues} setValues={setFormValues} />
          <Row gutter={24} style={{ marginTop: 20 }}>
            <Col span={24}></Col>
            <Col>
              <Button
                htmlType="submit"
                type="primary"
                onClick={handleSubmit}
                style={{ width: 150 }}
                disabled={formValues.opportunity.situationId !== '10'}
              >
                Atualizar
              </Button>
            </Col>
            <Col>
              <Button
                htmlType="reset"
                onClick={() => setFormValues(formDefaultValues)}
                type="default"
              >
                Reiniciar
              </Button>
            </Col>
          </Row>
        </div>
      </ContentBody>
    </>
  )
}
